import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUICK_CHIPS = [
  "Do you serve my area?",
  "What services do you offer?",
  "How do I book a free site visit?",
];

const WELCOME = {
  role: "assistant",
  content:
    "Hello! I'm the Anas Enterprises assistant. Ask me about our marble & granite services, Mumbai service areas, or how to book a free site visit.",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("ae_chat_session") || null);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, session_id: sessionId }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const evt of events) {
          const line = evt.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta };
                return copy;
              });
            }
            if (payload.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: payload.error };
                return copy;
              });
            }
            if (payload.done && payload.session_id) {
              setSessionId(payload.session_id);
              localStorage.setItem("ae_chat_session", payload.session_id);
            }
          } catch {
            /* partial JSON, ignore */
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Something went wrong. Please call +91 9867439947 for instant help.",
        };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chat-open-btn"
          type="button"
          aria-label="Open AI chat assistant"
          onClick={() => setOpen(true)}
          className="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 z-50 w-14 h-14 rounded-full bg-[#1e3a8a] hover:bg-[#1e40af] text-white shadow-xl flex items-center justify-center transition-colors"
        >
          <MessageCircle className="w-6 h-6" aria-hidden="true" />
        </button>
      )}

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] max-w-[360px] bg-white border border-slate-300 rounded-xl shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="bg-[#1e3a8a] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <img src="/ae-logo.png" alt="" className="h-8 w-auto object-contain bg-white rounded p-0.5" />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider leading-none">Anas Enterprises</p>
                <p className="text-[10px] text-blue-200 mt-0.5">AI Assistant • Powered by ChatGPT</p>
              </div>
            </div>
            <button
              data-testid="chat-close-btn"
              type="button"
              aria-label="Close chat"
              onClick={() => setOpen(false)}
              className="p-1.5 hover:bg-[#172554] rounded transition-colors"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>

          <div ref={listRef} data-testid="chat-messages" className="h-72 overflow-y-auto p-3 space-y-2.5 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-lg text-xs leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#1e3a8a] text-white rounded-br-sm"
                      : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm"
                  }`}
                >
                  {m.content || <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" aria-label="Assistant is typing" />}
                </div>
              </div>
            ))}
          </div>

          {messages.length === 1 && (
            <div className="px-3 pb-2 pt-1 bg-slate-50 flex flex-wrap gap-1.5">
              {QUICK_CHIPS.map((chip, i) => (
                <button
                  key={chip}
                  data-testid={`chat-chip-${i + 1}`}
                  type="button"
                  onClick={() => send(chip)}
                  className="text-[10px] font-semibold px-2.5 py-1.5 bg-blue-50 text-[#1e3a8a] border border-blue-200 rounded-full hover:bg-blue-100 transition-colors"
                >
                  {chip}
                </button>
              ))}
            </div>
          )}

          <form
            data-testid="chat-form"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex items-center gap-2 p-3 border-t border-slate-200 bg-white"
          >
            <input
              data-testid="chat-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about services, areas, booking..."
              className="flex-1 text-xs px-3 py-2.5 border border-slate-300 rounded-lg focus:border-[#1e3a8a] focus:outline-none"
            />
            <button
              data-testid="chat-send-btn"
              type="submit"
              disabled={sending || !input.trim()}
              aria-label="Send message"
              className="w-9 h-9 rounded-lg bg-[#1e3a8a] hover:bg-[#1e40af] text-white flex items-center justify-center transition-colors disabled:opacity-50"
            >
              <Send className="w-4 h-4" aria-hidden="true" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
